import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * TagInput — campo de entrada de opções como chips/tags.
 * Cada opção é adicionada individualmente (Enter ou botão "+"),
 * eliminando a necessidade de digitar vírgulas.
 *
 * @typedef {Object} TagInputProps
 * @property {string} [label] Rótulo do campo.
 * @property {string[]} [value=[]] Lista de opções atuais.
 * @property {(next: string[]) => void} onChange Callback com a nova lista.
 * @property {string} [placeholder] Placeholder do input.
 * @property {string} [hint] Texto de ajuda abaixo do campo.
 * @property {string} [className] Classes adicionais.
 */
export function TagInput({
  label,
  value = [],
  onChange,
  placeholder = 'Digite e pressione Enter',
  hint,
  className,
}) {
  const [draft, setDraft] = useState('');

  function addTag() {
    const tag = draft.trim();
    if (!tag) return;
    if (value.includes(tag)) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  }

  function removeTag(tag) {
    onChange(value.filter((t) => t !== tag));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  }

  return (
    <div className={cn('block', className)}>
      {label && (
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">
          {label}
        </span>
      )}
      <div className="rounded-xl border border-slate-200 bg-white px-2 py-1.5 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <div className="flex flex-wrap items-center gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-semibold text-primary"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-primary/70 transition-colors hover:bg-primary/20 hover:text-primary"
                aria-label={`Remover ${tag}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addTag}
            placeholder={value.length === 0 ? placeholder : ''}
            className="h-8 min-w-[120px] flex-1 bg-transparent px-1 text-sm text-ink outline-none placeholder:text-ink-faint"
          />
          <button
            type="button"
            onClick={addTag}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary-dark"
            aria-label="Adicionar opção"
          >
            <Plus size={15} />
          </button>
        </div>
      </div>
      {hint && <p className="mt-1.5 text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}
